import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { EventComment } from "@/types/content";
import { formatCount } from "@/utils/format";

function CommentItem({
  comment,
  liked,
  onToggleLike,
}: {
  comment: EventComment;
  liked: boolean;
  onToggleLike: (id: string) => void;
}) {
  const [showReplies, setShowReplies] = useState(true);
  const likeCount = comment.likes + (liked ? 1 : 0);

  return (
    <article className="flex gap-3.5 border-b border-background-200 py-5 last:border-b-0">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-100 font-heading text-base font-bold text-secondary-800">
        {comment.nickname.slice(0, 1)}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-foreground-950">{comment.nickname}</span>
          {comment.isHot && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold text-primary-700">
              <i className="ri-fire-line text-xs"></i>
              高赞神评
            </span>
          )}
          <span className="text-xs text-foreground-400">{comment.time}</span>
        </div>

        <p className="mt-2 text-sm leading-relaxed text-foreground-800 md:text-[15px]">{comment.content}</p>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={() => onToggleLike(comment.id)}
            aria-pressed={liked}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              liked
                ? "border-primary-400 bg-primary-100 text-primary-700"
                : "border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            <i className={`${liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} text-sm`}></i>
            赞同 {formatCount(likeCount)}
          </button>
          {comment.replies.length > 0 && (
            <button
              type="button"
              onClick={() => setShowReplies((v) => !v)}
              className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground-600 transition-colors hover:text-primary-600"
            >
              <i className="ri-chat-3-line text-sm"></i>
              {comment.replies.length} 条回复
              <i className={`text-sm ${showReplies ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
            </button>
          )}
        </div>

        {showReplies && comment.replies.length > 0 && (
          <ul className="mt-3 flex flex-col gap-2.5 rounded-md bg-background-100 p-3.5">
            {comment.replies.map((reply) => (
              <li key={`${comment.id}-${reply.nickname}`} className="text-xs leading-relaxed text-foreground-700">
                <span className="font-semibold text-secondary-700">{reply.nickname}：</span>
                {reply.content}
              </li>
            ))}
          </ul>
        )}
      </div>
    </article>
  );
}

export default function CommentSection({ comments }: { comments: EventComment[] }) {
  const [sort, setSort] = useState<"hot" | "new">("hot");
  const [likedIds, setLikedIds] = useState<string[]>([]);

  const list = useMemo(() => {
    const copy = [...comments];
    if (sort === "hot") copy.sort((a, b) => b.likes - a.likes);
    return copy;
  }, [comments, sort]);

  const toggleLike = (id: string) => {
    setLikedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <section id="comments" className="mt-10 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
      <div className="mb-2 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950">
          <i className="ri-chat-smile-2-line text-2xl text-primary-500"></i>
          围观评论区
          <span className="text-sm font-medium text-foreground-500">{comments.length} 条热评</span>
        </h2>

        <div className="inline-flex self-start rounded-full bg-background-100 p-1" role="tablist" aria-label="评论排序">
          <button
            type="button"
            role="tab"
            aria-selected={sort === "hot"}
            onClick={() => setSort("hot")}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              sort === "hot" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-950"
            }`}
          >
            最热评论
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={sort === "new"}
            onClick={() => setSort("new")}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              sort === "new" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-950"
            }`}
          >
            最新评论
          </button>
        </div>
      </div>

      <p className="mb-2 text-xs leading-relaxed text-foreground-500">
        以下评论均来自网友公开讨论的整理与节选，仅代表网友个人观点。喜欢哪条就点个赞同，理性吃瓜、友善发言。
      </p>

      <div>
        {list.map((comment) => (
          <CommentItem
            key={comment.id}
            comment={comment}
            liked={likedIds.includes(comment.id)}
            onToggleLike={toggleLike}
          />
        ))}
      </div>

      <div className="mt-6 flex flex-col items-start gap-3 rounded-md bg-secondary-50 p-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground-700">
          想第一时间参与讨论？加入吃瓜群，和历史围观群众一起聊现场。
        </p>
        <Link
          to="/about"
          className="inline-flex shrink-0 items-center whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600"
        >
          <i className="ri-group-line mr-1.5 text-base"></i>
          加入吃瓜群
        </Link>
      </div>
    </section>
  );
}